<template>
  <div class="ModuleCalculationForm">
    <form @submit="onCalculate">
      <span
        v-if="canI('RTH_MODULE_CALCULATIONS') && !multiLength"
        class="accordion-sub-title"
        >Performance</span
      >

      <text-field
        :value="moduleFlux"
        label="Module Flux (lm)"
        type="number"
        :active="inputLock === 'flux' && !isModuleFluxDisabled"
        @input="onModuleFluxChange"
        @click="onModuleFluxClick"
        :tooltipContent="
          'Specifying the module flux will use this field as an input,<br> otherwise it serves as an calculation output.'
        "
        :disabled="isModuleFluxDisabled"
      >
      </text-field>
      <text-field
        :value="moduleCurrent"
        label="Current (mA)"
        type="number"
        :active="inputLock === 'current'"
        @input="onModuleCurrentChange"
        @click="onModuleCurrentClick"
        :tooltipContent="
          'Specifying the current will use this field as an input,<br> otherwise it serves as an calculation output.'
        "
      >
      </text-field>

      <div class="match-single-current" v-if="solutionHasModule">
        <div class="custom-control custom-checkbox">
          <input
            type="checkbox"
            id="matchSingleCurrent"
            class="custom-control-input"
            v-model="matchSingleCurrent"
            value="true"
          />
          <label for="matchSingleCurrent" class="custom-control-label"
            >Match single current</label
          >
        </div>

        <div class="slider-container" v-if="matchSingleCurrent">
          <tooltip class="v-align" :content="'% deviation allowed'" />
          <div class="filter-name">mA +/- {{ singleCurrentValue }} %</div>
          <vue-slider
            :lazy="true"
            :dotSize="20"
            :duration="0.1"
            :min="1"
            :max="15"
            v-model="singleCurrentValue"
          ></vue-slider>
        </div>

        <easy-filter
          v-if="matchSingleCurrent"
          :group="singleCurrentFilter"
          @change="setModuleCurrentValue"
        ></easy-filter>
      </div>

      <template v-if="isAllowedLuminaireFluxAndLorInputs">
        <text-field
          :value="luminaireFlux"
          label="Luminaire flux (lm)"
          type="number"
          :active="inputLock === 'flux'"
          @click="onLuminaireFluxClick"
          @input="onLuminaireFluxChange"
          :tooltipContent="'The light output of your luminaire'"
        >
        </text-field>
      </template>

      <template v-if="isAllowedLuminaireFluxAndLorInputs">
        <text-field
          :value="lor"
          type="number"
          ref="inputLor"
          label="LOR (%)"
          @input="onLorChange"
          :tooltipContent="
            'Output of the luminaire, divided by its lamp output'
          "
        >
        </text-field>
      </template>

      <text-field
        v-if="!canI('RTH_MODULE_CALCULATIONS') || multiLength"
        :value="moduleTemperature"
        label="Tc (°C)"
        type="number"
        @input="onModuleTemperatureChange"
        :tooltipContent="
          'Temperature at the Tc point on the LED module.<br /> Tc point is indicated both on the PCB and in the associated datasheet.<br />Tc cannot be lower than Ta by agreement.'
        "
      >
      </text-field>

      <template v-if="canI('RTH_MODULE_CALCULATIONS') && !multiLength">
        <span v-if="canI('RTH_MODULE_CALCULATIONS')" class="accordion-sub-title"
          >Ambient temperature</span
        >
        <text-field
          :value="moduleTemperature"
          label="Tc (°C)"
          type="number"
          :active="disableRTHInput !== 'tc'"
          @input="onModuleTemperatureChange"
          @click="onModuleTemperatureClick"
          :tooltipContent="
            'Temperature at the Tc point on the LED module.<br /> Tc point is indicated both on the PCB and in the associated datasheet.<br />Tc cannot be lower than Ta by agreement.'
          "
        >
        </text-field>
        <text-field
          :value="moduleTa"
          label="Ta (°C)"
          type="number"
          @input="onModuleTaChange"
          :tooltipContent="
            'The Ta (°C) value always needs to be equal or greater than the Tc (°C) value'
          "
        >
        </text-field>
        <text-field
          :value="moduleRTH"
          label="Rth hs-a (°C/W)"
          type="number"
          step="0.01"
          :active="disableRTHInput !== 'rth'"
          @input="onModuleRTHChange"
          @click="onModuleRTHClick"
          :tooltipContent="
            'Thermal resistance from heat sink to ambient. <br>Rth cannot be lower than 0 by agreement.'
          "
        >
        </text-field>
      </template>

      <div @click="clearValues" class="clear pb-4">
        Clear values
      </div>
      <button type="submit" class="btn btn-secondary btn-block btn-sm">
        Calculate
      </button>
    </form>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { EventBus } from "@/eventbus";
import { EFFICACY_BASED_CALCULATIONS } from "@/service/Constants";
import ValidateErrorModal from "@/components/modals/ValidateErrorModal";
import ChangedInputWithSolutionModal from "@/components/modals/ChangedInputWithSolutionModal";
import VueSlider from "vue-slider-component";

export default {
  name: "ModuleCalculationForm",
  modals: {
    ValidateErrorModal,
    ChangedInputWithSolutionModal
  },
  components: {
    VueSlider
  },
  data: () => {
    return {
      inputLockModel: null,
      inputLockModelRTH: null,
      matchSingleCurrent: false,
      singleCurrentValue: 5,
      selectedSingleCurrent: null
    };
  },
  computed: {
    ...mapState("brain/modules", [
      "moduleFlux",
      "moduleCurrent",
      "luminaireFlux",
      "moduleTemperature",
      "lor",
      "inputLock",
      "module",
      "inputLockRTH",
      "moduleTa",
      "moduleRTH"
    ]),

    ...mapGetters("brain/solutions", [
      "multiLength",
      "hasSolutions",
      "solution"
    ]),

    ...mapGetters("brain/solutions", {
      activeSolution: "active"
    }),

    ...mapState("brain/drivers", {
      driverFilters: "filters"
    }),

    ...mapGetters("brain/ui", ["dontShowChangedInputModal"]),

    solutionHasModule() {
      return this.hasSolutions && this.solution.module;
    },

    disableRTHInput() {
      return this.inputLockRTH === "tc" ? "rth" : "tc";
    },

    isAllowedLuminaireFluxAndLorInputs() {
      return this.canI(EFFICACY_BASED_CALCULATIONS);
    },

    isModuleFluxDisabled() {
      return this.isAllowedLuminaireFluxAndLorInputs && !!this.luminaireFlux;
    },

    singleCurrentFilter() {
      const scFilter = this.driverFilters.find(
        f => f.title === "Single currents"
      );
      const min = (1 - this.singleCurrentValue / 100) * this.moduleCurrent;
      const max = (1 + this.singleCurrentValue / 100) * this.moduleCurrent;
      return {
        ...scFilter,
        title: "Choose: Current (mA)",
        filters: [
          {
            ...scFilter.filters[0],
            availableValues: scFilter.filters[0].availableValues.filter(
              v => v.value >= min && v.value <= max
            )
          }
        ]
      };
    }
  },

  methods: {
    onModuleFluxChange(value) {
      this.$store.commit("brain/modules/setModuleFlux", value.target.value);
    },
    onModuleFluxClick() {
      this.inputLockModel = "flux";
    },
    onModuleCurrentChange(value) {
      this.$store.commit("brain/modules/setModuleCurrent", value.target.value);
    },
    onModuleCurrentClick() {
      this.inputLockModel = "current";
    },
    onLuminaireFluxChange(value) {
      this.$store.commit("brain/modules/setLuminaireFlux", value.target.value);
    },
    onLuminaireFluxClick() {
      this.inputLockModel = "flux";
    },
    onLorChange(value) {
      if (value.target.value <= 100) {
        this.$store.commit("brain/modules/setLor", value.target.value);
      } else {
        this.$nextTick(() => {
          this.$store.commit("brain/modules/setLor", 100);
        });
      }
    },
    onModuleTemperatureChange(value) {
      this.$store.commit(
        "brain/modules/setModuleTemperature",
        value.target.value
      );
    },
    onModuleTemperatureClick() {
      this.inputLockModelRTH = "tc";
    },
    onModuleTaChange(value) {
      this.$store.commit("brain/modules/setModuleTa", value.target.value);
    },
    onModuleRTHChange(value) {
      this.$store.commit("brain/modules/setModuleRTH", value.target.value);
    },
    onModuleRTHClick() {
      this.inputLockModelRTH = "rth";
    },
    async onCalculate(event) {
      event.preventDefault();
      if (this.moduleRTH < 0) {
        this.$snotify.error("The Rth hs-a (°C/W) cannot be below 0", {
          position: "leftTop"
        });
        return;
      }
      if (
        this.moduleTemperature !== null &&
        this.moduleTemperature !== "" &&
        this.moduleTemperature < this.moduleTa
      ) {
        this.$snotify.error("The Tc (°C) value cannot be lower than Ta (°C)", {
          position: "leftTop"
        });
        return;
      }

      if (this.luminaireFlux) {
        this.$store.commit(
          "brain/modules/setModuleFlux",
          Math.round(this.luminaireFlux / (this.lor / 100))
        );
      } else if (this.moduleFlux) {
        this.$store.commit(
          "brain/modules/setLuminaireFlux",
          Math.round(this.moduleFlux * (this.lor / 100))
        );
      }

      if (this.multiLength) {
        this.$store.commit("brain/global/setBusy", true);
        await this.$store.dispatch("brain/modules/calculateMultiLengthModules");
        await EventBus.$emit("recalculateDrivers");
        this.$store.commit("brain/global/setBusy", false);
      } else {
        if (this.module != null) {
          this.$store.commit("brain/global/setBusy", true);
          this.$store
            .dispatch("brain/solutions/validateSolution", {})
            .then(res => {
              if (res.causes.length) {
                this.$modalRouter.push({
                  name: "ValidateErrorModal",
                  props: {
                    causes: res.causes,
                    onContinue: () => {
                      this.$store.commit(
                        "brain/modules/setIncompatibleModule",
                        this.module
                      );
                      this.$store.commit("brain/modules/setModule", null);
                      this.$store.commit(
                        "brain/modules/saveModuleCalculationParameters"
                      );
                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true
                      });
                    },
                    onUndo: () => {
                      // Reset module calculation parameters to the last known working state
                      this.$store.commit(
                        "brain/modules/resetModuleCalculationParameters"
                      );
                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true
                      });
                    },
                    onRemoveDriver: () => {
                      this.$store.commit(
                        "brain/solutions/removeSensor",
                        this.activeSolution
                      );
                      this.$store.commit(
                        "brain/solutions/removeBridge",
                        this.activeSolution
                      );
                      this.$store.dispatch(
                        "brain/solutions/removeDriver",
                        this.activeSolution
                      );
                      this.$store.dispatch(
                        "brain/solutions/removeEmergencyDriver",
                        this.activeSolution
                      );
                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true
                      });
                    }
                  }
                });
              } else if (
                this.solutionHasModule &&
                !this.dontShowChangedInputModal
              ) {
                this.$modalRouter.push({
                  name: "ChangedInputWithSolutionModal",
                  props: { show: true },
                  model: "show",
                  on: {
                    overwriteCurrentSolution: () => {
                      // Remove driver
                      this.$store.commit(
                        "brain/solutions/removeSensor",
                        this.activeSolution
                      );
                      this.$store.commit(
                        "brain/solutions/removeBridge",
                        this.activeSolution
                      );
                      this.$store.dispatch(
                        "brain/solutions/removeDriver",
                        this.activeSolution
                      );
                      this.$store.dispatch(
                        "brain/solutions/removeEmergencyDriver",
                        this.activeSolution
                      );

                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true,
                        done: () => {
                          this.$store
                            .dispatch("brain/solutions/add", {
                              type: "module",
                              addNew: false,
                              data: this.module
                            })
                            .then(() => {
                              EventBus.$emit("recalculateDrivers");
                            });
                        }
                      });
                    },
                    addToNewSolution: () => {
                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true,
                        done: () => {
                          this.$store
                            .dispatch("brain/solutions/add", {
                              type: "module",
                              addNew: true,
                              data: this.module
                            })
                            .then(() => {
                              EventBus.$emit("recalculateDrivers");
                            });
                        }
                      });
                    },
                    undo: () => {
                      // Reset module calculation parameters to the last known working state
                      this.$store.commit(
                        "brain/modules/resetModuleCalculationParameters"
                      );
                      EventBus.$emit("brain/modules/handleFilterChange", {
                        e: null,
                        addToHistory: true,
                        calcChange: true
                      });
                    }
                  }
                });
              } else {
                // The calculation parameters were valid
                EventBus.$emit("brain/modules/handleFilterChange", {
                  e: null,
                  addToHistory: true,
                  calcChange: true
                });

                //T2109 0302 - Change driver current based on new module current
                this.$store.commit(
                  "brain/drivers/setDriverCurrent",
                  this.moduleCurrent
                );
              }
              this.$store.commit("brain/global/setBusy", false);
            })
            .catch(err => {
              console.log(err);
              this.$store.commit("brain/global/setBusy", false);
            });
        } else {
          EventBus.$emit("brain/modules/handleFilterChange", {
            e: null,
            addToHistory: true,
            calcChange: true
          });
        }
      }
    },
    clearValues() {
      this.$store.dispatch("brain/modules/clearCalculatedPerformance");
    },
    setModuleCurrentValue(e) {
      const value = {
        target: {
          value: e.values[0]
        }
      };
      this.onModuleCurrentChange(value);
      this.matchSingleCurrent = false;
    }
  },
  created() {
    this.inputLockModel = this.inputLock;
    this.inputLockModelRTH = this.inputLockRTH;
  },
  watch: {
    inputLockModel: function(inputLock) {
      this.$store.commit("brain/modules/setInputLock", inputLock);
    },
    inputLock: function(inputLock) {
      if (inputLock !== this.inputLockModel) {
        this.inputLockModel = inputLock;
      }
    },
    inputLockModelRTH: function(inputLock) {
      this.$store.commit("brain/modules/setInputLockRTH", inputLock);
    },
    inputLockRTH: function(inputLock) {
      if (inputLock !== this.inputLockModelRTH) {
        this.inputLockModelRTH = inputLock;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.ModuleCalculationForm {
  padding: 0 1.5rem;

  label {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .easy-tooltip {
      align-self: flex-end;
    }
  }

  .match-single-current {
    background-color: #f9f9f9;
    margin-top: -16px;
    margin-bottom: 1rem;
    padding: 4px 10px 4px 10px;

    ::v-deep {
      .filter-heading,
      .filter-content {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
}

.filter-name {
  padding-left: 1.25rem;
}

.accordion-sub-title {
  margin-bottom: 16px;
  display: block;
  font-size: 12px;
}

.custom-control-label {
  margin: 0 0 0.5rem 0;
}

.custom-control-label:after,
.custom-control-label:before {
  top: 0.15625rem;
  width: 1rem;
  height: 1rem;
}

.formGroupInput {
  border: 1px solid $mid-gray-600;
  &::placeholder {
    color: $mid-gray-600;
  }
}
.formGroupInputRadio {
  border: 1px solid $mid-gray-600;
  &::placeholder {
    color: $mid-gray-600;
  }
}

.form-text {
  color: $blue;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
}

.btn-secondary,
.btn-outline-secondary {
  font-size: 1.1rem;
  font-weight: normal;
  cursor: pointer;
}

.clear {
  align-self: flex-end;
  color: $blue;
  text-decoration: underline;
  cursor: pointer;
}

.pb-4 {
  padding-bottom: 0 !important;
  margin-bottom: 15px !important;
}
</style>

<style lang="scss">
.ModuleCalculationForm {
  .easy-tooltip {
    margin: 0 2px;
    float: right;
  }
}
</style>
